import React, { useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { askConfirm } from '../../../shared/AppDialogs';

// The rebuild is one synchronous request with no incremental progress to
// report, so this is a spinner rather than a real N-of-M progress bar —
// same reasoning as PlatformSettingsPage's Regenerate Thumbnails action.
const SPIN_KEYFRAMES = '@keyframes system-admin-spin { to { transform: rotate(360deg); } }';

// Local dev-machine tooling that has no place on a tenant-facing settings
// screen — the only screen in the app that can destroy the local dev
// database, see issue #234.
function SystemAdminPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState('');

  const handleRefresh = async () => {
    if (!(await askConfirm('This will permanently erase your local dev database and replace it with a fresh copy of production data, then apply any pending migrations.\n\nThis cannot be undone. Continue?', { confirmLabel: 'Refresh from Production', destructive: true }))) return;

    setRefreshing(true);
    setResult('');
    dismissError();
    try {
      const res = await axios.post('/api/system-admin/refresh-dev-from-prod');
      setResult(res.data.message || 'Done.');
    } catch (err) {
      showError(err, 'Refresh failed');
    }
    setRefreshing(false);
  };

  return (
    <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>System Admin</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>Local dev-machine tools.</p>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e3e7ee' }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>Refresh Dev from Prod</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          Replaces this local dev database's schema and data entirely with a
          current copy of production, then applies any migrations not yet
          reflected in that copy, then fetches whatever Media Library files
          (product images, entity logos) are missing locally straight from
          production. Refuses to run in production, or if there are
          uncommitted local git changes.
        </div>
        <style>{SPIN_KEYFRAMES}</style>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Replaces the local dev database with a fresh copy of production"
          style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 6, border: 'none', background: refreshing ? '#90caf9' : '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: refreshing ? 'not-allowed' : 'pointer' }}
        >
          {refreshing && <Loader2 size={15} strokeWidth={2.5} style={{ animation: 'system-admin-spin 0.8s linear infinite' }} />}
          {refreshing ? 'Refreshing...' : 'Refresh Dev from Prod'}
        </button>
        {result && (
          <div style={{ color: '#2e7d32', marginTop: 10, fontSize: 13 }}>
            {result} Restart your local dev server to pick it up.
          </div>
        )}
      </div>
    </div>
  );
}

export default SystemAdminPage;
